import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Star } from 'lucide-react';
import { audio } from '../core/audio';
import { recordSkill } from '../core/storage';

const GOAL = 3;

// Slow-motion tap targets for hand-eye coordination. Three modes: bubble | star | dots.
export function MotionTap({ activity, onComplete }) {
  const [hits, setHits] = useState(0);
  const [items, setItems] = useState([]);
  const [lit, setLit] = useState(0);
  const [shake, setShake] = useState(0);
  const reduced = useReducedMotion();
  const done = useRef(false);
  const idc = useRef(0);
  const L = activity.level || 1;

  useEffect(() => { const t = setTimeout(() => audio.playVoice(activity.voice), 400); return () => clearTimeout(t); }, []); // eslint-disable-line

  useEffect(() => {
    if (activity.mode === 'dots') return;
    const concurrent = activity.mode === 'star' ? 1 : Math.min(L, 3);
    const iv = setInterval(() => {
      setItems((cur) => (cur.length >= concurrent ? cur : [...cur, { id: idc.current++, x: 10 + Math.random() * 68 }]));
    }, 800);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (activity.mode !== 'dots') return;
    const ms = L === 1 ? 2600 : L === 2 ? 1900 : 1500;
    const iv = setInterval(() => setLit((l) => (l + 1) % 3), ms);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const registerHit = () => {
    audio.playSfx('correct');
    recordSkill(activity.skill, true);
    window.dispatchEvent(new CustomEvent('pp-answer', { detail: { correct: true } }));
    setHits((h) => {
      const nh = h + 1;
      if (nh >= GOAL && !done.current) {
        done.current = true;
        setTimeout(() => onComplete({ id: activity.id, correct: true, attempts: GOAL, hints: 0, skill: activity.skill }), 700);
      }
      return nh;
    });
  };

  const dur = activity.mode === 'star'
    ? (L === 1 ? 9 : L === 2 ? 7 : 5.5)
    : (L === 1 ? 9 : L === 2 ? 7.5 : 6);

  return (
    <div className="flex-1 relative overflow-hidden" data-testid={`activity-${activity.id}`}>
      <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-2 bg-white/80 backdrop-blur rounded-full px-4 py-2 shadow z-10" data-testid="motion-progress">
        {Array.from({ length: GOAL }).map((_, i) => (
          <Star key={i} size={22} className={i < hits ? 'text-sunshine fill-sunshine' : 'text-navy/20'} />
        ))}
      </div>

      {activity.mode === 'bubble' && items.map((b) => (
        <motion.button key={b.id} data-testid={`bubble-${b.id}`} aria-label="bubble"
          className="absolute bottom-[-130px] rounded-full flex items-center justify-center"
          style={{ left: `${b.x}%`, width: 116, height: 116, background: 'radial-gradient(circle at 35% 30%, rgba(255,255,255,0.9), rgba(140,205,255,0.55))', border: '4px solid rgba(255,255,255,0.85)', boxShadow: '0 8px 24px rgba(29,53,87,0.2)' }}
          initial={{ y: 0, opacity: 1 }}
          animate={{ y: reduced ? -320 : -760 }}
          transition={{ duration: reduced ? dur / 2 : dur, ease: 'linear' }}
          onAnimationComplete={() => setItems((cur) => cur.filter((i) => i.id !== b.id))}
          onClick={() => { setItems((cur) => cur.filter((i) => i.id !== b.id)); registerHit(); }} />
      ))}

      {activity.mode === 'star' && items.map((s) => (
        <motion.button key={s.id} data-testid={`drift-star-${s.id}`} aria-label="star"
          className="absolute left-[-130px] flex items-center justify-center"
          style={{ top: `${18 + Math.random() * 45}%`, width: 120, height: 120 }}
          initial={{ x: 0 }}
          animate={{ x: (typeof window !== 'undefined' ? window.innerWidth : 900) + 180 }}
          transition={{ duration: dur, ease: 'linear' }}
          onAnimationComplete={() => setItems((cur) => cur.filter((i) => i.id !== s.id))}
          onClick={() => { setItems((cur) => cur.filter((i) => i.id !== s.id)); registerHit(); }}>
          <Star size={100} className="text-sunshine fill-sunshine drop-shadow-xl" />
        </motion.button>
      ))}

      {activity.mode === 'dots' && (
        <div className="absolute inset-0 flex items-center justify-center gap-8">
          {[0, 1, 2].map((i) => {
            const isLit = lit === i;
            return (
              <motion.button key={i} data-testid={`dot-${i}`}
                animate={isLit ? { scale: [1, 1.12, 1] } : shake && !isLit ? {} : {}}
                transition={{ duration: 0.6 }}
                onClick={() => { if (isLit) registerHit(); else { setShake((s) => s + 1); audio.playSfx('retry'); window.dispatchEvent(new CustomEvent('pp-answer', { detail: { correct: false } })); } }}
                className="rounded-full"
                style={{ width: 128, height: 128, background: isLit ? '#FFD166' : 'rgba(255,255,255,0.55)',
                  border: '6px solid rgba(255,255,255,0.9)',
                  boxShadow: isLit ? '0 0 40px 8px rgba(255,209,102,0.75), inset 0 -8px 0 rgba(0,0,0,0.1)' : 'inset 0 -8px 0 rgba(0,0,0,0.08)' }} />
            );
          })}
        </div>
      )}
    </div>
  );
}
